/**
 * HTTP-клиент к FastAPI (CRA proxy → backend в dev).
 */

const AUTH_TOKEN_KEY = "stihiya_auth_token";
const AUTH_USER_KEY = "stihiya_auth_user";
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;

export function getStoredAuthToken() {
  try {
    return window.sessionStorage.getItem(AUTH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredAuthToken(token) {
  try {
    if (token) {
      window.sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch {
    // ignore storage failures in restricted webviews
  }
}

function clearStoredAuthSession() {
  setStoredAuthToken(null);
  try {
    window.sessionStorage.removeItem(AUTH_USER_KEY);
  } catch {
    // ignore storage failures in restricted webviews
  }
}

function getAdminHeaders({ withJsonContentType = true } = {}) {
  const headers = {};
  if (withJsonContentType) {
    headers["Content-Type"] = "application/json";
  }
  const token = getStoredAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  const initData = window.Telegram?.WebApp?.initData;
  if (initData) {
    headers["telegram-init-data"] = initData;
  }
  return headers;
}

async function parseJsonSafe(response, fallback) {
  try {
    return await response.json();
  } catch {
    return fallback;
  }
}

function extractErrorMessage(data, response) {
  if (typeof data?.detail === "string") {
    return data.detail;
  }
  if (Array.isArray(data?.detail)) {
    return data.detail.map((x) => x?.msg || "").filter(Boolean).join("; ");
  }
  return response.statusText || "Request failed";
}

async function requestJson(path, options = {}) {
  const {
    method = "GET",
    headers = undefined,
    body = undefined,
    fallback = {},
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = options;
  const controller = new AbortController();
  const timerId = window.setTimeout(() => controller.abort(), Math.max(1, Number(timeoutMs) || 1));
  let response;
  try {
    response = await fetch(path, { method, headers, body, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Превышено время ожидания ответа. Попробуйте снова.");
    }
    throw new Error("Не удалось выполнить запрос. Проверьте подключение к сети.");
  } finally {
    window.clearTimeout(timerId);
  }
  const data = await parseJsonSafe(response, fallback);
  if (!response.ok) {
    if (response.status === 401) {
      clearStoredAuthSession();
      throw new Error("Сессия истекла. Откройте миниапп заново.");
    }
    throw new Error(extractErrorMessage(data, response));
  }
  return data;
}

async function requestArray(path, options = {}) {
  const data = await requestJson(path, { ...options, fallback: [] });
  return Array.isArray(data) ? data : [];
}

export async function postTelegramAuth(initData) {
  return requestJson("/api/auth/telegram", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
    fallback: {},
  });
}

export async function getAuthMe() {
  return requestJson("/api/auth/me", {
    headers: getAdminHeaders(),
    fallback: {},
  });
}

/** Персональная реферальная ссылка (t.me/...?startapp=ref_<telegram_id> для Mini App). */
export async function getUsersMeReferral() {
  return requestJson("/api/users/me/referral", {
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function getEvents() {
  return requestArray("/api/events");
}

/** Публичный список акций (только с is_active=true на сервере). */
export async function getActions() {
  return requestArray("/api/actions");
}

export async function getMyRaffleEntries() {
  return requestArray("/api/actions/my-raffles", {
    headers: getAdminHeaders({ withJsonContentType: false }),
  });
}

export async function postJoinRaffleAction(actionId) {
  return requestJson(`/api/actions/${actionId}/join`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

export async function getMarketItems() {
  return requestArray("/api/market");
}

export async function getMarketSections() {
  return requestArray("/api/market/sections");
}

export async function getMenuCategories() {
  return requestArray("/api/menu/categories");
}

export async function getMenuItems() {
  return requestArray("/api/menu/items");
}

export async function getMarketItemById(merchId) {
  return requestJson(`/api/market/${merchId}`, { fallback: {} });
}

export async function reserveMarketItem(merchId, paymentType) {
  return requestJson(`/api/market/${merchId}/reserve`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({ payment_type: paymentType }),
    fallback: {},
  });
}

export async function getMyMarketReservations() {
  return requestArray("/api/market/my/reservations", {
    headers: getAdminHeaders(),
  });
}

export async function getAdminEvents() {
  return requestArray("/api/admin/events", { headers: getAdminHeaders() });
}

export async function createAdminEvent(payload) {
  return requestJson("/api/admin/events", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminEvent(eventId, payload) {
  return requestJson(`/api/admin/events/${eventId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminEvent(eventId) {
  return requestJson(`/api/admin/events/${eventId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function seedAdminTournament(eventId) {
  return requestJson(`/api/admin/events/${eventId}/tournament/seed`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

export async function getAdminTournament(eventId) {
  return requestJson(`/api/admin/events/${eventId}/tournament`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
    fallback: {},
  });
}

export async function postAdminTournamentMatchResult(eventId, matchId, winnerUserId) {
  return requestJson(`/api/admin/events/${eventId}/tournament/matches/${matchId}/result`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({ winner_user_id: Number(winnerUserId) }),
    fallback: {},
  });
}

export async function getAdminActions() {
  return requestArray("/api/admin/actions", { headers: getAdminHeaders() });
}

export async function createAdminAction(payload) {
  return requestJson("/api/admin/actions", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminAction(actionId, payload) {
  return requestJson(`/api/admin/actions/${actionId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminAction(actionId) {
  return requestJson(`/api/admin/actions/${actionId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function getAdminActionEntries(actionId) {
  return requestArray(`/api/admin/actions/${actionId}/entries`, {
    headers: getAdminHeaders(),
  });
}

export async function getAdminMarketItems() {
  return requestArray("/api/admin/market", { headers: getAdminHeaders() });
}

export async function getAdminMarketSections() {
  return requestArray("/api/admin/market/sections", { headers: getAdminHeaders() });
}

export async function getAdminMarketReservations() {
  return requestArray("/api/admin/market/reservations", { headers: getAdminHeaders() });
}

export async function getAdminMenuCategories() {
  return requestArray("/api/admin/menu/categories", { headers: getAdminHeaders() });
}

export async function createAdminMenuCategory(payload) {
  return requestJson("/api/admin/menu/categories", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminMenuCategory(categoryId, payload) {
  return requestJson(`/api/admin/menu/categories/${categoryId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminMenuCategory(categoryId) {
  return requestJson(`/api/admin/menu/categories/${categoryId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function getAdminMenuItems() {
  return requestArray("/api/admin/menu/items", { headers: getAdminHeaders() });
}

export async function createAdminMenuItem(payload) {
  return requestJson("/api/admin/menu/items", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminMenuItem(itemId, payload) {
  return requestJson(`/api/admin/menu/items/${itemId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminMenuItem(itemId) {
  return requestJson(`/api/admin/menu/items/${itemId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function createAdminMarketSection(payload) {
  return requestJson("/api/admin/market/sections", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminMarketSection(sectionId, payload) {
  return requestJson(`/api/admin/market/sections/${sectionId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminMarketSection(sectionId) {
  return requestJson(`/api/admin/market/sections/${sectionId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function createAdminMarketItem(payload) {
  return requestJson("/api/admin/market", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function patchAdminMarketItem(merchId, payload) {
  return requestJson(`/api/admin/market/${merchId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

export async function deleteAdminMarketItem(merchId) {
  return requestJson(`/api/admin/market/${merchId}`, {
    method: "DELETE",
    headers: getAdminHeaders(),
    fallback: {},
  });
}

export async function uploadAdminMarketImage(file) {
  const body = new FormData();
  body.append("image", file);
  return requestJson("/api/admin/market/upload-image", {
    method: "POST",
    headers: getAdminHeaders({ withJsonContentType: false }),
    body,
    fallback: {},
  });
}

export async function uploadAdminEventImage(file) {
  const body = new FormData();
  body.append("image", file);
  return requestJson("/api/admin/events/upload-image", {
    method: "POST",
    headers: getAdminHeaders({ withJsonContentType: false }),
    body,
    fallback: {},
  });
}

/** @param {{ amount?: number, receipt_rub?: number }} payload */
export async function generateAdminBonusToken(payload) {
  return requestJson("/api/admin/bonus/generate", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** @param {{ amount: number }} payload */
export async function generateAdminBonusDebitToken(payload) {
  return requestJson("/api/admin/bonus/generate-debit", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Опрос уведомлений админу после сканирования его QR (успех / недостаточно бонусов). */
export async function pollAdminBonusRedeemEvents(token) {
  const q = new URLSearchParams({ token: String(token) });
  return requestJson(`/api/admin/bonus/redeem-events?${q}`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
    fallback: { event: null },
  });
}

export async function redeemBonusToken(token) {
  return requestJson("/api/bonus/redeem", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({ token }),
    fallback: {},
  });
}

export async function getBonusBalance() {
  return requestJson("/api/bonus/balance", {
    headers: getAdminHeaders(),
    fallback: {},
  });
}

/** Одноразовая акция: ФИО + опциональный ник (бонус начисляется через 24ч). */
export async function postBonusPromoProfile(payload) {
  return requestJson("/api/bonus/promo-profile", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Одноразовая акция: дата рождения (бонус начисляется через 24ч). */
export async function postBonusPromoBirthday(payload) {
  return requestJson("/api/bonus/promo-birthday", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Одноразовая акция: телефон (бонус начисляется через 24ч). */
export async function postBonusPromoPhone(payload) {
  return requestJson("/api/bonus/promo-phone", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Настройки шага «подписка на канал»: configured + channel_url (если задан PROMO_CHANNEL_USERNAME на сервере). */
export async function getBonusPromoChannel() {
  return requestJson("/api/bonus/promo-channel", {
    headers: getAdminHeaders({ withJsonContentType: false }),
    fallback: { configured: false, channel_url: null },
  });
}

/** Подтвердить подписку на канал (без +100; +300 за 4 шага начисляется через 24ч после завершения комплекта). */
export async function postBonusPromoChannelVerify() {
  return requestJson("/api/bonus/promo-channel-verify", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

/** Ссылки на страницы отзывов (Яндекс, 2ГИС) — с сервера, с учётом .env. */
export async function getBonusPromoReviewLinks() {
  return requestJson("/api/bonus/promo-review-links", {
    headers: getAdminHeaders({ withJsonContentType: false }),
    fallback: { yandex_url: "", gis_url: "" },
  });
}

/** Одноразово: переход на отзывы Яндекс.Карт (бонус начисляется через 24ч). */
export async function postBonusPromoYandexReview() {
  return requestJson("/api/bonus/promo-yandex-review", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

/** Одноразово: переход на отзывы 2ГИС (бонус начисляется через 24ч). */
export async function postBonusPromo2gisReview() {
  return requestJson("/api/bonus/promo-2gis-review", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

/** Админ: база гостей — участники закреплённых акций (поиск по ФИО, телефону, TG и т.д.). */
export async function getAdminGuestBase({ q = "", limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  return requestArray(`/api/admin/bonus/promo-profiles?${params}`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
  });
}

/** Админ: список для управления ролью хостес (без поиска — текущие хостес, с поиском — все пользователи). */
export async function getAdminHostessUsers({ q = "", limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  return requestArray(`/api/admin/bonus/hostess-users?${params}`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
  });
}

/** Админ: изменить ФИО/ник из акции (только если гость уже прошёл акцию). */
export async function patchAdminGuestProfile(userId, payload) {
  return requestJson(`/api/admin/bonus/guest-profile/${userId}`, {
    method: "PATCH",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Админ: полностью удалить гостя из БД (история бонусов, QR, брони). */
export async function deleteAdminGuestProfile(userId) {
  return requestJson(`/api/admin/bonus/guest-profile/${userId}`, {
    method: "DELETE",
    headers: getAdminHeaders({ withJsonContentType: false }),
    fallback: {},
  });
}

/** Админ: вернуть бонусы, удержанные из‑за отписки от промо-канала. */
export async function postAdminRestoreChannelBalance(userId) {
  return requestJson(`/api/admin/bonus/guest-profile/${userId}/restore-channel-balance`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
    fallback: {},
  });
}

/** Админ: назначить/снять роль хостес у гостя. */
export async function postAdminSetGuestHostess(userId, enabled) {
  return requestJson(`/api/admin/bonus/guest-profile/${userId}/hostess`, {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify({ enabled: Boolean(enabled) }),
    fallback: {},
  });
}

/** Админ: история бонусных операций конкретного гостя. */
export async function getAdminGuestBonusHistory(userId, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  return requestArray(`/api/admin/bonus/guest-profile/${userId}/history?${params}`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
  });
}

export async function getBonusHistory(limit = 50) {
  const q = new URLSearchParams({ limit: String(limit) });
  return requestArray(`/api/bonus/history?${q}`, {
    headers: getAdminHeaders(),
  });
}

/** Админ-тест: сохранить результат раунда мини-игры. */
export async function postGameRun(payload) {
  return requestJson("/api/game/runs", {
    method: "POST",
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
    fallback: {},
  });
}

/** Админ-тест: лидерборд (лучший результат пользователя за период). */
export async function getGameLeaderboard(period = "day", limit = 50) {
  const q = new URLSearchParams({ period, limit: String(limit) });
  return requestArray(`/api/game/leaderboard?${q}`, {
    headers: getAdminHeaders({ withJsonContentType: false }),
  });
}
